import data from '@/data/hangeul';
import create from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import _cloneDeep from 'lodash/cloneDeep';
import { ArtMesh, Data, DataRow } from '@/types';

type SortBy = 'area' | 'year' | 'sizeX' | 'sizeY';

interface IStore {
  data: Data;
  artId: number;
  indexMap: number[];
  setArtId: (artId: number) => void;
  getArt: (artId: number) => DataRow | undefined;
  getArtNow: () => DataRow | undefined;
  setMesh: (artId: number, mesh: ArtMesh) => void;
  setSortBy: (sortBy: SortBy) => void;
  setClickable: (clickable: boolean) => void;
  sortBy: SortBy;
  clickable: boolean;
}

const sortDataByArea = (_data: Data) => {
  const _d = _cloneDeep(_data);
  const indexMap: number[] = [];
  _d.forEach((d, i) => {
    indexMap[d.order] = i;
  });
  _d[0].selected = true;
  const defaultArtId = _d[0].order;
  return { defaultArtId, sortedData: _d, indexMap };
};

const getInitialStore = (): Pick<
  IStore,
  'indexMap' | 'data' | 'artId' | 'sortBy' | 'clickable'
> => {
  const { indexMap, sortedData, defaultArtId } = sortDataByArea(data);

  return {
    indexMap,
    data: sortedData,
    artId: defaultArtId,
    sortBy: 'area',
    clickable: false,
  };
};

export const useStore = create<IStore>(
  subscribeWithSelector((set, get) => {
    const store = getInitialStore();
    return {
      ...store,
      setClickable: (clickable) => {
        set((state) => {
          return { ...state, clickable };
        });
      },
      setSortBy: (sortBy) => {
        set((state) => {
          return { ...state, sortBy };
        });
      },
      setArtId: (artId) => {
        set((state) => {
          // WARNING this code will not work
          // state.artId = artId
          // mutation is not monitored by Zustand
          // data must be sliced

          const prevIndex = state.indexMap[state.artId];
          const newData = state.data.slice();
          const newIndex = state.indexMap[artId];
          if (newData[newIndex]) {
            newData[newIndex].selected = true;
          }
          if (state.data[prevIndex]) {
            newData[prevIndex].selected = false;
          }
          return { ...state, data: newData, artId };
        });
      },
      getArt(artId) {
        const state = get();
        const index = state.indexMap[artId];
        return state.data[index];
      },
      getArtNow() {
        const state = get();
        const index = state.indexMap[state.artId];
        return state.data[index];
      },
      setMesh(artId, mesh) {
        set((state) => {
          // binding mesh to state
          // runs only once per an object
          const newData = data.slice();
          const index = state.indexMap[artId];
          newData[index].mesh = mesh;
          return { ...state, data: newData };
        });
      },
    };
  })
);
